/**
 * @fileoverview
 * Application Permission Constants
 * Defines all permissions used throughout the application
 * Must match backend permission definitions exactly
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

/**
 * Masters Module Permissions
 */

// User Master Permissions
export const USER_PERMISSIONS = {
  CREATE: 'masters.user.create',
  READ: 'masters.user.read',
  UPDATE: 'masters.user.update',
  DELETE: 'masters.user.delete',
} as const;

// Role Master Permissions
export const ROLE_PERMISSIONS = {
  CREATE: 'masters.role.create',
  READ: 'masters.role.read',
  UPDATE: 'masters.role.update',
  DELETE: 'masters.role.delete',
} as const;

// Permission Master Permissions
export const PERMISSION_PERMISSIONS = {
  CREATE: 'masters.permission.create',
  READ: 'masters.permission.read',
  UPDATE: 'masters.permission.update',
  DELETE: 'masters.permission.delete',
} as const;

// Department Master Permissions
export const DEPARTMENT_PERMISSIONS = {
  CREATE: 'masters.department.create',
  READ: 'masters.department.read',
  UPDATE: 'masters.department.update',
  DELETE: 'masters.department.delete',
} as const;

// Region Master Permissions
export const REGION_PERMISSIONS = {
  CREATE: 'region.create',
  READ: 'region.read',
  UPDATE: 'region.update',
  DELETE: 'region.delete',
} as const;

// Branch Master Permissions
export const BRANCH_PERMISSIONS = {
  CREATE: 'branch.create',
  READ: 'branch.read',
  UPDATE: 'branch.update',
  DELETE: 'branch.delete',
} as const;

// Audit Type Master Permissions
export const AUDIT_TYPE_PERMISSIONS = {
  CREATE: 'masters.audit-type.create',
  READ: 'masters.audit-type.read',
  UPDATE: 'masters.audit-type.update',
  DELETE: 'masters.audit-type.delete',
} as const;

// Audit Pattern Template Master Permissions
export const AUDIT_PATTERN_TEMPLATE_PERMISSIONS = {
  CREATE: 'audit-pattern-template.create',
  READ: 'audit-pattern-template.read',
  UPDATE: 'audit-pattern-template.update',
  DELETE: 'audit-pattern-template.delete',
} as const;

// Particular Category Master Permissions
export const PARTICULAR_CATEGORY_PERMISSIONS = {
  CREATE: 'particular-categories.create',
  READ: 'particular-categories.read',
  UPDATE: 'particular-categories.update',
  DELETE: 'particular-categories.delete',
} as const;

// Generic Master Permissions
export const GENERIC_PERMISSIONS = {
  CREATE: 'masters.generic.create',
  READ: 'masters.generic.read',
  UPDATE: 'masters.generic.update',
  DELETE: 'masters.generic.delete',
} as const;

/**
 * Settings Module Permissions
 */

// Audit Schedule Permissions
export const AUDIT_SCHEDULE_PERMISSIONS = {
  CREATE: 'settings.audit-schedule.create',
  READ: 'settings.audit-schedule.read',
  UPDATE: 'settings.audit-schedule.update',
  DELETE: 'settings.audit-schedule.delete',
} as const;

// Dynamic Form Permissions
export const DYNAMIC_FORM_PERMISSIONS = {
  CREATE: 'settings.dynamic-form.create',
  READ: 'settings.dynamic-form.read',
  UPDATE: 'settings.dynamic-form.update',
  DELETE: 'settings.dynamic-form.delete',
} as const;

// Database Integration Permissions
export const DATABASE_INTEGRATION_PERMISSIONS = {
  CREATE: 'settings.database-integration.create',
  READ: 'settings.database-integration.read',
  UPDATE: 'settings.database-integration.update',
  DELETE: 'settings.database-integration.delete',
} as const;

/**
 * Audit Module Permissions
 */

// Cash Audit Permissions
export const CASH_AUDIT_PERMISSIONS = {
  CREATE: 'cash.create',
  READ: 'cash.read',
  UPDATE: 'cash.update',
  DELETE: 'cash.delete',
} as const;

// Register Statutory Permissions
export const REGISTER_STATUTORY_PERMISSIONS = {
  CREATE: 'register-statutory.create',
  READ: 'register-statutory.read',
  UPDATE: 'register-statutory.update',
  DELETE: 'register-statutory.delete',
} as const;

// Gold Audit Permissions
export const GOLD_AUDIT_PERMISSIONS = {
  CREATE: 'gold.create',
  READ: 'gold.read',
  UPDATE: 'gold.update',
  DELETE: 'gold.delete',
} as const;

// Gold Valuation Tool Permissions
export const GOLD_VALUATION_TOOL_PERMISSIONS = {
  CREATE: 'gold.valuation-tool.create',
  READ: 'gold.valuation-tool.read',
  UPDATE: 'gold.valuation-tool.update',
  DELETE: 'gold.valuation-tool.delete',
} as const;

// Gold Verification Permissions
export const GOLD_VERIFICATION_PERMISSIONS = {
  CREATE: 'gold.gold-verification.create',
  READ: 'gold.gold-verification.read',
  UPDATE: 'gold.gold-verification.update',
  DELETE: 'gold.gold-verification.delete',
} as const;

// Packet Inventory Permissions
export const PACKET_INVENTORY_PERMISSIONS = {
  CREATE: 'packet.create',
  READ: 'packet.read',
  UPDATE: 'packet.update',
  DELETE: 'packet.delete',
} as const;

// Surveillance Permissions
export const SURVEILLANCE_PERMISSIONS = {
  CREATE: 'surveillance.create',
  READ: 'surveillance.read',
  UPDATE: 'surveillance.update',
  DELETE: 'surveillance.delete',
} as const;

// Surveillance Routine Checking Permissions
export const ROUTINE_CHECKING_PERMISSIONS = {
  CREATE: 'surveillance.routine-checking.create',
  READ: 'surveillance.routine-checking.read',
  UPDATE: 'surveillance.routine-checking.update',
  DELETE: 'surveillance.routine-checking.delete',
} as const;

// Surveillance Loan Process Checking Permissions
export const LOAN_PROCESS_CHECKING_PERMISSIONS = {
  CREATE: 'surveillance.loan-process-checking.create',
  READ: 'surveillance.loan-process-checking.read',
  UPDATE: 'surveillance.loan-process-checking.update',
  DELETE: 'surveillance.loan-process-checking.delete',
} as const;

// Tare Weight Permissions
export const TARE_WEIGHT_PERMISSIONS = {
  CREATE: 'tare.create',
  READ: 'tare.read',
  UPDATE: 'tare.update',
  DELETE: 'tare.delete',
} as const;

// Online Permissions
export const ONLINE_PERMISSIONS = {
  CREATE: 'online.create',
  READ: 'online.read',
  UPDATE: 'online.update',
  DELETE: 'online.delete',
} as const;

// Online KYC and Disbursement Permissions
export const KYC_DISBURSEMENT_PERMISSIONS = {
  CREATE: 'online.kyc-and-disbursement.create',
  READ: 'online.kyc-and-disbursement.read',
  UPDATE: 'online.kyc-and-disbursement.update',
  DELETE: 'online.kyc-and-disbursement.delete',
} as const;

// Online Release Permissions
export const RELEASE_PERMISSIONS = {
  CREATE: 'online.release.create',
  READ: 'online.release.read',
  UPDATE: 'online.release.update',
  DELETE: 'online.release.delete',
} as const;

// Query Management Permissions
export const QUERY_MANAGEMENT_PERMISSIONS = {
  CREATE: 'query.create',
  READ: 'query.read',
  UPDATE: 'query.update',
  DELETE: 'query.delete',
} as const;

// Reports Permissions
export const REPORTS_PERMISSIONS = {
  CREATE: 'reports.create',
  READ: 'reports.read',
  UPDATE: 'reports.update',
  DELETE: 'reports.delete',
} as const;

// Asset Management Permissions
export const ASSET_MANAGEMENT_PERMISSIONS = {
  CREATE: 'asset-management.create',
  READ: 'asset-management.read',
  UPDATE: 'asset-management.update',
  DELETE: 'asset-management.delete',
} as const;

// Asset Entry Permissions
export const ASSET_ENTRY_PERMISSIONS = {
  CREATE: 'asset-management.asset-entry.create',
  READ: 'asset-management.asset-entry.read',
  UPDATE: 'asset-management.asset-entry.update',
  DELETE: 'asset-management.asset-entry.delete',
} as const;

// Asset Audit Permissions
export const ASSET_AUDIT_PERMISSIONS = {
  CREATE: 'asset-management.asset-audit.create',
  READ: 'asset-management.asset-audit.read',
  UPDATE: 'asset-management.asset-audit.update',
  DELETE: 'asset-management.asset-audit.delete',
} as const;

// Vault Key Entry Permissions
export const VAULT_KEY_ENTRY_PERMISSIONS = {
  CREATE: 'asset-management.vault-key-entry.create',
  READ: 'asset-management.vault-key-entry.read',
  UPDATE: 'asset-management.vault-key-entry.update',
  DELETE: 'asset-management.vault-key-entry.delete',
} as const;

// Key Movement Sender Permissions
export const KEY_MOVEMENT_SENDER_PERMISSIONS = {
  CREATE: 'asset-management.key-movement-sender.create',
  READ: 'asset-management.key-movement-sender.read',
  UPDATE: 'asset-management.key-movement-sender.update',
  DELETE: 'asset-management.key-movement-sender.delete',
} as const;

// Key Movement Receiver Permissions
export const KEY_MOVEMENT_RECEIVER_PERMISSIONS = {
  CREATE: 'asset-management.key-movement-receiver.create',
  READ: 'asset-management.key-movement-receiver.read',
  UPDATE: 'asset-management.key-movement-receiver.update',
  DELETE: 'asset-management.key-movement-receiver.delete',
} as const;


// Key Audit Permissions
export const KEY_AUDIT_PERMISSIONS = {
  CREATE: 'asset-management.key-audit.create',
  READ: 'asset-management.key-audit.read',
  UPDATE: 'asset-management.key-audit.update',
  DELETE: 'asset-management.key-audit.delete',
} as const;

// Branch Audit Permissions
export const BRANCH_AUDIT_PERMISSIONS = {
  CREATE: 'branch-audit.create',
  READ: 'branch-audit.read',
  UPDATE: 'branch-audit.update',
  DELETE: 'branch-audit.delete',
} as const;

// Security Permissions
export const SECURITY_PERMISSIONS = {
  CREATE: 'security.create',
  READ: 'security.read',
  UPDATE: 'security.update',
  DELETE: 'security.delete',
} as const;

// Score Card Permissions (score card list / audit view)
export const SCORECARD_PERMISSIONS = {
  CREATE: 'score-card.create',
  READ: 'score-card.read',
  UPDATE: 'score-card.update',
  DELETE: 'score-card.delete',
} as const;

// Score Card Parameter Permissions (score card builder - parameters, scoring model)
export const SCORECARD_PARAMETER_PERMISSIONS = {
  CREATE: 'score-card-builder.parameters.create',
  READ: 'score-card-builder.parameters.read',
  UPDATE: 'score-card-builder.parameters.update',
  DELETE: 'score-card-builder.parameters.delete',
} as const;

// Score Card Scoring Model Permissions (score card builder - scoring model)
export const SCORECARD_SCORING_MODEL_PERMISSIONS = {
  CREATE: 'score-card-builder.scoring-model.create',
  READ: 'score-card-builder.scoring-model.read',
  UPDATE: 'score-card-builder.scoring-model.update',
  DELETE: 'score-card-builder.scoring-model.delete',
} as const;


/**
 * Combined Permissions Object
 * For easy access to all permission groups
 */
export const PERMISSIONS = {
  // Masters
  USER: USER_PERMISSIONS,
  ROLE: ROLE_PERMISSIONS,
  PERMISSION: PERMISSION_PERMISSIONS,
  DEPARTMENT: DEPARTMENT_PERMISSIONS,
  REGION: REGION_PERMISSIONS,
  BRANCH: BRANCH_PERMISSIONS,
  AUDIT_TYPE: AUDIT_TYPE_PERMISSIONS,
  AUDIT_PATTERN_TEMPLATE: AUDIT_PATTERN_TEMPLATE_PERMISSIONS,
  PARTICULAR_CATEGORY: PARTICULAR_CATEGORY_PERMISSIONS,
  GENERIC: GENERIC_PERMISSIONS,
  
  // Settings
  AUDIT_SCHEDULE: AUDIT_SCHEDULE_PERMISSIONS,
  DYNAMIC_FORM: DYNAMIC_FORM_PERMISSIONS,

  // Audit
  CASH_AUDIT: CASH_AUDIT_PERMISSIONS,
  REGISTER_STATUTORY: REGISTER_STATUTORY_PERMISSIONS,
  GOLD_AUDIT: GOLD_AUDIT_PERMISSIONS,
  GOLD_VALUATION_TOOL: GOLD_VALUATION_TOOL_PERMISSIONS,
  GOLD_VERIFICATION: GOLD_VERIFICATION_PERMISSIONS,
  
  // Other Features
  PACKET_INVENTORY: PACKET_INVENTORY_PERMISSIONS,
  SURVEILLANCE: SURVEILLANCE_PERMISSIONS,
  ROUTINE_CHECKING: ROUTINE_CHECKING_PERMISSIONS,
  LOAN_PROCESS_CHECKING: LOAN_PROCESS_CHECKING_PERMISSIONS,
  TARE_WEIGHT: TARE_WEIGHT_PERMISSIONS,
  ONLINE: ONLINE_PERMISSIONS,
  KYC_DISBURSEMENT: KYC_DISBURSEMENT_PERMISSIONS,
  RELEASE: RELEASE_PERMISSIONS,
  QUERY_MANAGEMENT: QUERY_MANAGEMENT_PERMISSIONS,
  REPORTS: REPORTS_PERMISSIONS,
  ASSET_MANAGEMENT: ASSET_MANAGEMENT_PERMISSIONS,
  ASSET_ENTRY: ASSET_ENTRY_PERMISSIONS,
  ASSET_AUDIT: ASSET_AUDIT_PERMISSIONS,
  VAULT_KEY_ENTRY: VAULT_KEY_ENTRY_PERMISSIONS,
  KEY_MOVEMENT_SENDER: KEY_MOVEMENT_SENDER_PERMISSIONS,
  KEY_MOVEMENT_RECEIVER: KEY_MOVEMENT_RECEIVER_PERMISSIONS,
  KEY_AUDIT: KEY_AUDIT_PERMISSIONS,
  BRANCH_AUDIT: BRANCH_AUDIT_PERMISSIONS,
  SECURITY: SECURITY_PERMISSIONS,
  SCORE_CARD: SCORECARD_PERMISSIONS,
  SCORECARD_PARAMETER: SCORECARD_PARAMETER_PERMISSIONS,
} as const;

/**
 * Type Definitions
 */
export type UserPermission = typeof USER_PERMISSIONS[keyof typeof USER_PERMISSIONS];
export type RolePermission = typeof ROLE_PERMISSIONS[keyof typeof ROLE_PERMISSIONS];
export type PermissionPermission = typeof PERMISSION_PERMISSIONS[keyof typeof PERMISSION_PERMISSIONS];
export type DepartmentPermission = typeof DEPARTMENT_PERMISSIONS[keyof typeof DEPARTMENT_PERMISSIONS];
export type RegionPermission = typeof REGION_PERMISSIONS[keyof typeof REGION_PERMISSIONS];
export type BranchPermission = typeof BRANCH_PERMISSIONS[keyof typeof BRANCH_PERMISSIONS];
export type AuditTypePermission = typeof AUDIT_TYPE_PERMISSIONS[keyof typeof AUDIT_TYPE_PERMISSIONS];
export type AuditPatternTemplatePermission = typeof AUDIT_PATTERN_TEMPLATE_PERMISSIONS[keyof typeof AUDIT_PATTERN_TEMPLATE_PERMISSIONS];
export type ParticularCategoryPermission = typeof PARTICULAR_CATEGORY_PERMISSIONS[keyof typeof PARTICULAR_CATEGORY_PERMISSIONS];
export type GenericPermission = typeof GENERIC_PERMISSIONS[keyof typeof GENERIC_PERMISSIONS];
export type AuditSchedulePermission = typeof AUDIT_SCHEDULE_PERMISSIONS[keyof typeof AUDIT_SCHEDULE_PERMISSIONS];
export type DynamicFormPermission = typeof DYNAMIC_FORM_PERMISSIONS[keyof typeof DYNAMIC_FORM_PERMISSIONS];
export type CashAuditPermission = typeof CASH_AUDIT_PERMISSIONS[keyof typeof CASH_AUDIT_PERMISSIONS];
export type RegisterStatutoryPermission = typeof REGISTER_STATUTORY_PERMISSIONS[keyof typeof REGISTER_STATUTORY_PERMISSIONS];
export type GoldAuditPermission = typeof GOLD_AUDIT_PERMISSIONS[keyof typeof GOLD_AUDIT_PERMISSIONS];
export type GoldValuationToolPermission = typeof GOLD_VALUATION_TOOL_PERMISSIONS[keyof typeof GOLD_VALUATION_TOOL_PERMISSIONS];
export type GoldVerificationPermission = typeof GOLD_VERIFICATION_PERMISSIONS[keyof typeof GOLD_VERIFICATION_PERMISSIONS];
export type PacketInventoryPermission = typeof PACKET_INVENTORY_PERMISSIONS[keyof typeof PACKET_INVENTORY_PERMISSIONS];
export type SurveillancePermission = typeof SURVEILLANCE_PERMISSIONS[keyof typeof SURVEILLANCE_PERMISSIONS];
export type RoutineCheckingPermission = typeof ROUTINE_CHECKING_PERMISSIONS[keyof typeof ROUTINE_CHECKING_PERMISSIONS];
export type LoanProcessCheckingPermission = typeof LOAN_PROCESS_CHECKING_PERMISSIONS[keyof typeof LOAN_PROCESS_CHECKING_PERMISSIONS];
export type TareWeightPermission = typeof TARE_WEIGHT_PERMISSIONS[keyof typeof TARE_WEIGHT_PERMISSIONS];
export type OnlinePermission = typeof ONLINE_PERMISSIONS[keyof typeof ONLINE_PERMISSIONS];
export type KycDisbursementPermission = typeof KYC_DISBURSEMENT_PERMISSIONS[keyof typeof KYC_DISBURSEMENT_PERMISSIONS];
export type ReleasePermission = typeof RELEASE_PERMISSIONS[keyof typeof RELEASE_PERMISSIONS];
export type QueryManagementPermission = typeof QUERY_MANAGEMENT_PERMISSIONS[keyof typeof QUERY_MANAGEMENT_PERMISSIONS];
export type ReportsPermission = typeof REPORTS_PERMISSIONS[keyof typeof REPORTS_PERMISSIONS];
export type AssetManagementPermission = typeof ASSET_MANAGEMENT_PERMISSIONS[keyof typeof ASSET_MANAGEMENT_PERMISSIONS];
export type AssetEntryPermission = typeof ASSET_ENTRY_PERMISSIONS[keyof typeof ASSET_ENTRY_PERMISSIONS];
export type AssetAuditPermission = typeof ASSET_AUDIT_PERMISSIONS[keyof typeof ASSET_AUDIT_PERMISSIONS];
export type VaultKeyEntryPermission = typeof VAULT_KEY_ENTRY_PERMISSIONS[keyof typeof VAULT_KEY_ENTRY_PERMISSIONS];
export type KeyMovementSenderPermission = typeof KEY_MOVEMENT_SENDER_PERMISSIONS[keyof typeof KEY_MOVEMENT_SENDER_PERMISSIONS];
export type KeyMovementReceiverPermission = typeof KEY_MOVEMENT_RECEIVER_PERMISSIONS[keyof typeof KEY_MOVEMENT_RECEIVER_PERMISSIONS];
export type KeyAuditPermission = typeof KEY_AUDIT_PERMISSIONS[keyof typeof KEY_AUDIT_PERMISSIONS];
export type BranchAuditPermission = typeof BRANCH_AUDIT_PERMISSIONS[keyof typeof BRANCH_AUDIT_PERMISSIONS];
export type SecurityPermission = typeof SECURITY_PERMISSIONS[keyof typeof SECURITY_PERMISSIONS];
export type ScoreCardPermission = typeof SCORECARD_PERMISSIONS[keyof typeof SCORECARD_PERMISSIONS];
export type ScoreCardParameterPermission = typeof SCORECARD_PARAMETER_PERMISSIONS[keyof typeof SCORECARD_PARAMETER_PERMISSIONS];
export type ScoreCardScoringModelPermission = typeof SCORECARD_SCORING_MODEL_PERMISSIONS[keyof typeof SCORECARD_SCORING_MODEL_PERMISSIONS];

/**
 * Union type of all permissions
 */
export type AppPermission = 
  | UserPermission
  | RolePermission
  | PermissionPermission
  | DepartmentPermission
  | RegionPermission
  | BranchPermission
  | AuditTypePermission
  | AuditPatternTemplatePermission
  | ParticularCategoryPermission
  | GenericPermission
  | AuditSchedulePermission
  | DynamicFormPermission
  | CashAuditPermission
  | RegisterStatutoryPermission
  | GoldAuditPermission
  | GoldValuationToolPermission
  | GoldVerificationPermission
  | PacketInventoryPermission
  | SurveillancePermission
  | RoutineCheckingPermission
  | LoanProcessCheckingPermission
  | TareWeightPermission
  | OnlinePermission
  | KycDisbursementPermission
  | ReleasePermission
  | QueryManagementPermission
  | ReportsPermission
  | AssetManagementPermission
  | AssetEntryPermission
  | AssetAuditPermission
  | VaultKeyEntryPermission
  | KeyMovementSenderPermission
  | KeyMovementReceiverPermission
  | KeyAuditPermission
  | BranchAuditPermission
  | SecurityPermission
  | ScoreCardPermission
  | ScoreCardParameterPermission
  | ScoreCardScoringModelPermission;

/**
 * Helper function to get all permission values as an array
 */
export function getAllPermissions(): string[] {
  return Object.values(PERMISSIONS)
    .flatMap(group => Object.values(group));
}

/**
 * Helper function to check if a string is a valid permission
 */
export function isValidPermission(permission: string): permission is AppPermission {
  return getAllPermissions().includes(permission);
}
